import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ContentType, SubType } from './api/types';
import { AuthProvider, useAuth } from './AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedBrandRoute from './components/brand/ProtectedBrandRoute';
import { BrandProvider } from './contexts/BrandContext';
import { EmulationProvider } from './contexts/EmulationContext';
import { AyrshareConnectionProvider } from './contexts/AyrshareConnectionContext';
import { PublishStatusProvider } from './contexts/PublishStatusContext';
import { FeedbackCountsProvider } from './contexts/FeedbackCountsContext';
import { TooltipCopyProvider } from './contexts/TooltipCopyContext';
import { PostMetricsProvider } from './contexts/PostMetricsContext';
import { QueueStatusProvider, QueueStatusScope } from './contexts/QueueStatusContext';
import { ProfileSheetProvider, useProfileSheet } from './contexts/ProfileSheetContext';
import { AgentProvider } from './contexts/AgentContext';
import ProfileSheet from './components/profile/ProfileSheet';
import EmulationBanner from './components/brand/EmulationBanner';
// Eager imports for deep-link pages — keep them out of Suspense so they
// can't get stuck on a stale React.lazy promise after an HMR reload.
import TendPreviewPage from './pages/TendPreviewPage';
import TendPostPage from './pages/TendPostPage';

// Import the new dynamic homepage that pulls real data from backend API
const DynamicHomePage = React.lazy(() => import('./pages/DynamicHomePage'));
const MarketingHomePage = React.lazy(() => import('./pages/MarketingHomePage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const PublisherPage = React.lazy(() => import('./pages/PublisherPage'));
const BrandPage = React.lazy(() => import('./pages/BrandPage'));
const TendedContentPage = React.lazy(() => import('./pages/TendedContentPage'));
const PublishedContentPage = React.lazy(() => import('./pages/PublishedContentPage'));
// The trio's shared chrome shell — lazy so BrandHeaderV3's dropdown code
// stays out of the main bundle
const BrandShellLayout = React.lazy(() => import('./components/brand/BrandShellLayout'));
const ArticlePage = React.lazy(() => import('./pages/ArticlePage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const ChangePasswordPage = React.lazy(() => import('./pages/ChangePasswordPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const WelcomePage = React.lazy(() => import('./pages/WelcomePage'));
const AcceptInvitationPage = React.lazy(() => import('./pages/AcceptInvitationPage'));
const DropPage = React.lazy(() => import('./pages/DropPage'));
const IconMigration = React.lazy(() => import('./pages/IconMigration'));

// "/" resolves per auth state: brand users go straight to their brand page,
// everyone else (logged out, or signed in with no verified brand) gets the
// marketing site. Brandless signed-in users get the profile sheet auto-opened
// with the no-brand block (handled inside ProfileSheet).
function RootRoute() {
  const { user, isAuthenticated, initialized } = useAuth();

  // Gate on the one-time session check only — NOT isLoading, which also
  // flips during login/register and would unmount the marketing page
  // mid-submit (white screen under the sheet).
  if (!initialized) return null;

  const verifiedBrand = user?.brands?.find((b) => b.verified && b.slug);
  if (isAuthenticated && verifiedBrand) {
    // A pending session-expiry return target wins over the brand home —
    // this render-phase Navigate used to race ProfileSheet's post-auth
    // effect and strand users on their brand page instead of where the
    // expiry interrupted them.
    let returnUrl: string | null = null;
    try {
      returnUrl = sessionStorage.getItem('tend:returnUrl');
      if (returnUrl) sessionStorage.removeItem('tend:returnUrl');
    } catch { /* ignore */ }
    return <Navigate to={returnUrl || `/${verifiedBrand.slug}`} replace />;
  }
  return <MarketingHomePage />;
}

// Emailed token deep links (/welcome?token=, /reset-password?token=) render
// the marketing homepage with the profile sheet auto-opened in the matching
// view. The legacy standalone pages remain at /legacy/* as backups.
function TokenSheetRoute({ view }: { view: 'create-password' | 'reset-password' }) {
  const { openSheet } = useProfileSheet();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Deferred open (macrotask): the provider's close-on-navigation effect
    // flushes after this child effect, so a synchronous open here would be
    // immediately closed on client-side navigation. setTimeout (not rAF —
    // rAF never fires in background tabs) lands after the whole commit.
    // Mount-only: re-running on searchParams identity churn would fight
    // user navigation inside the sheet.
    const id = setTimeout(() => openSheet(view, { token: searchParams.get('token') }), 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <MarketingHomePage />;
}

// Component to handle static asset requests
function StaticAssetHandler() {
  const location = useLocation();
  
  // If this is a request for a static asset, redirect to the actual file
  if (location.pathname.startsWith('/assets/')) {
    window.location.href = location.pathname;
    return <div>Redirecting to asset...</div>;
  }
  
  // If not an asset request, show 404
  return <div>Page not found</div>;
}

// App-level filter state management
interface FilterState {
  activeContentTypes: Set<ContentType>;
  activeSubTypes: Set<SubType>;
}

function App() {
  // Initialize filter state with persistence
  const [filterState, setFilterState] = useState<FilterState>(() => {
    try {
      const savedContentTypes = localStorage.getItem('nu-activeContentTypes');
      const savedSubTypes = localStorage.getItem('nu-activeSubTypes');
      
      return {
        activeContentTypes: savedContentTypes 
          ? new Set(JSON.parse(savedContentTypes)) 
          : new Set([ContentType.TEXT, ContentType.AUDIO, ContentType.VIDEO, ContentType.IMAGE]),
        activeSubTypes: savedSubTypes 
          ? new Set(JSON.parse(savedSubTypes))
          : new Set(Object.values(SubType))
      };
    } catch (e) {
      console.error('Error loading saved filters:', e);
      return {
        activeContentTypes: new Set([ContentType.TEXT, ContentType.AUDIO, ContentType.VIDEO, ContentType.IMAGE]),
        activeSubTypes: new Set(Object.values(SubType))
      };
    }
  });

  // Persist filter changes to localStorage
  useEffect(() => {
    localStorage.setItem('nu-activeContentTypes', JSON.stringify(Array.from(filterState.activeContentTypes)));
  }, [filterState.activeContentTypes]);

  useEffect(() => {
    localStorage.setItem('nu-activeSubTypes', JSON.stringify(Array.from(filterState.activeSubTypes)));
  }, [filterState.activeSubTypes]);

  // Filter update functions - memoized to prevent re-renders
  const updateContentTypes = useCallback((newContentTypes: Set<ContentType>) => {
    setFilterState(prev => ({
      ...prev,
      activeContentTypes: new Set(newContentTypes)
    }));
  }, []);

  const updateSubTypes = useCallback((newSubTypes: Set<SubType>) => {
    setFilterState(prev => ({
      ...prev,
      activeSubTypes: new Set(newSubTypes)
    }));
  }, []);

  // Convert to API filter format - memoized to prevent unnecessary re-renders
  const apiFilters = useMemo(() => {
    if (filterState.activeContentTypes.size === 0) {
      return ['all']; // Default to all content when nothing selected
    } else if (filterState.activeContentTypes.size === 4) {
      return ['all']; // All content
    } else {
      return Array.from(filterState.activeContentTypes);
    }
  }, [filterState.activeContentTypes]);

  return (
    <AuthProvider>
      <EmulationProvider>
        <BrandProvider>
          <AyrshareConnectionProvider>
          <PublishStatusProvider>
          <FeedbackCountsProvider>
          <TooltipCopyProvider>
          <PostMetricsProvider>
          <QueueStatusProvider>
          <Router>
            <QueueStatusScope />
            <ProfileSheetProvider>
            <AgentProvider>
            <React.Suspense fallback={<div>Loading...</div>}>
              {/* Emulation Banner - Shows when SuperAdmin is emulating another user */}
              <EmulationBanner />

              <Routes>
            {/* Public routes */}
            <Route path="/" element={<RootRoute />} />
            {/* The aggregated content feed (the old homepage), kept at /all */}
            <Route
              path="/all"
              element={
                <DynamicHomePage
                  filterState={filterState}
                  apiFilters={apiFilters}
                />
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            {/* Emailed token links open the sheet over the marketing page */}
            <Route path="/reset-password" element={<TokenSheetRoute view="reset-password" />} />
            <Route path="/welcome" element={<TokenSheetRoute view="create-password" />} />
            {/* Legacy standalone pages kept as backups for the token flows */}
            <Route path="/legacy/reset-password" element={<ResetPasswordPage />} />
            <Route path="/legacy/welcome" element={<WelcomePage />} />
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            {/* TEND MCP server: a person drags a file into a reservation an agent made */}
            <Route path="/drop/:token" element={<DropPage />} />
            <Route path="/static" element={<HomePage />} />
            <Route path="/assets/*" element={<StaticAssetHandler />} />

            {/* Protected routes */}
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/publisher/:publisherName"
              element={<PublisherPage />}
            />

            {/* Icon Migration Tool - Admin Only */}
            <Route
              path="/admin/icon-migration"
              element={
                <ProtectedRoute>
                  <IconMigration />
                </ProtectedRoute>
              }
            />

            {/* Article page route - MUST BE BEFORE /:slug to avoid conflicts */}
            <Route
              path="/:slug/article/:contentId"
              element={
                <ProtectedBrandRoute>
                  <ArticlePage />
                </ProtectedBrandRoute>
              }
            />

            {/* TEND deep-link routes - single item MUST come before aggregate */}
            <Route
              path="/:slug/tend/:contentId/item/:itemId"
              element={
                <ProtectedBrandRoute>
                  <TendPostPage />
                </ProtectedBrandRoute>
              }
            />
            <Route
              path="/:slug/tend/:contentId"
              element={
                <ProtectedBrandRoute>
                  <TendPreviewPage />
                </ProtectedBrandRoute>
              }
            />

            {/* Brand page trio — one layout route so the chrome (header,
                sky, brand resolution) mounts ONCE and persists across
                Original|Tended|Published navigation. MUST BE LAST to avoid
                catching other routes. */}
            <Route
              path="/:slug"
              element={
                <ProtectedBrandRoute>
                  <BrandShellLayout />
                </ProtectedBrandRoute>
              }
            >
              <Route index element={<BrandPage />} />
              <Route path="tended" element={<TendedContentPage />} />
              <Route path="published" element={<PublishedContentPage />} />
            </Route>
              </Routes>
            </React.Suspense>
            {/* Mounted OUTSIDE the route Suspense: lazy-chunk loads must not
                unmount the sheet mid-animation. */}
            <ProfileSheet
              filterState={filterState}
              updateContentTypes={updateContentTypes}
              updateSubTypes={updateSubTypes}
            />
            </AgentProvider>
            </ProfileSheetProvider>
          </Router>
          </QueueStatusProvider>
          </PostMetricsProvider>
          </TooltipCopyProvider>
          </FeedbackCountsProvider>
          </PublishStatusProvider>
          </AyrshareConnectionProvider>
        </BrandProvider>
      </EmulationProvider>
    </AuthProvider>
  );
}

export default App;